import { toast } from 'sonner';
import { useFragment, useMutation } from '@apollo/client/react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { AppMembershipRemoveDocument } from 'src/graphql/actions/appMemberships/appMembershipRemove.mutation';
import { AppMembershipFragment } from 'src/graphql/actions/appMemberships/appMemberships.fragment';
import { type IAppMembership } from 'src/graphql/schema';
import { Button } from '@/components/ui/button';
import { useApp } from 'src/context/app';

type Props = {
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  successCallback?: () => void;
};

const RemoveMembershipDialog = ({ id, open, onOpenChange, successCallback }: Props) => {
  const [removeAppMembership, { loading }] = useMutation(AppMembershipRemoveDocument);
  const { app } = useApp();
  const membership = useFragment<IAppMembership>({
    fragment: AppMembershipFragment,
    from: { __typename: 'AppMembership', id },
  });
  const userName = membership?.data.user?.name;

  const onRemove = async () => {
    try {
      await removeAppMembership({
        variables: {
          appMembershipId: id,
        },
      });

      onOpenChange(false);
      toast.success(`${userName} removed from ${app.name}`);
      successCallback?.();
    } catch {
      toast.error('Failed to remove member access');
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Remove {userName} from {app.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            They&apos;ll immediately lose access to this application and every product in it. You can invite them again
            later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onRemove} disabled={loading} isLoading={loading}>
            Remove member
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveMembershipDialog;
